/**
 * WMS Profile Configuration
 *
 * Defines KPI sets, process flow steps, nav visibility, and dashboard themes
 * per warehouse vertical.  Tenant sets `warehouseProfile` in config;
 * the dashboard + nav system reads the active profile from here.
 *
 * Profile enum aligns with the current runtime: cfs | ecommerce | general |
 * manufacturing | distribution | yard | hybrid.
 * We also accept legacy aliases such as 3pl, metals, and ecommerce warehouse.
 */

import type { LucideIcon } from 'lucide-react';
import {
  Activity,
  ArrowDownLeft,
  ArrowRight,
  BarChart3,
  Boxes,
  CalendarDays,
  ClipboardCheck,
  ClipboardList,
  Cog,
  Container,
  FileSpreadsheet,
  FileUp,
  Forklift,
  Layers,
  ListOrdered,
  Package,
  PackageCheck,
  Repeat,
  Rocket,
  SearchCheck,
  Shield,
  ShieldCheck,
  ShoppingCart,
  Timer,
  Truck,
  TruckIcon,
  Undo2,
  Warehouse,
} from 'lucide-react';

// ─── Profile type ──────────────────────────────────

export type WmsProfileKey =
  | 'cfs'
  | 'ecommerce'
  | 'general'      // primary profile for general / 3PL warehouses
  | 'manufacturing'
  | 'distribution'
  | 'yard'
  | 'hybrid';      // shows everything (default)

// ─── KPI definition ────────────────────────────────

export interface WmsKpiDef {
  key: string;
  label: string;
  /** Key path into DashboardData.counts */
  dataKey: keyof WmsCountKeys;
  icon: LucideIcon;
  tone: string;
  route: string;
}

/** Union of all count keys that KPIs can reference */
export type WmsCountKeys = {
  skus: number;
  inboundOrders: number;
  outboundOrders: number;
  cycleCounts: number;
  inventoryRecords: number;
  pickLists: number;
  asns: number;
};

// ─── Flow step definition ──────────────────────────

export interface WmsFlowStep {
  key: string;
  phase: string;
  title: string;
  desc: string;
  route: string;
  icon: LucideIcon;
}

// ─── Dashboard theme ───────────────────────────────

export interface WmsProfileTheme {
  heroGradient: string;
  heroIcon: LucideIcon;
  heroLabel: string;
  heroSubtitle: string;
}

// ─── Full profile preset ───────────────────────────

export interface WmsProfile {
  key: WmsProfileKey;
  label: string;
  description: string;
  theme: WmsProfileTheme;
  kpis: WmsKpiDef[];
  flowSteps: WmsFlowStep[];
  /** Nav item IDs to show. undefined = show all (hybrid). */
  visibleNavIds?: string[];
  /** Quick‐link cards displayed below operational stats */
  quickLinks: Array<{ label: string; desc: string; route: string; icon: LucideIcon }>;
}

// ─── Shared KPI pieces ─────────────────────────────

const KPI_SKUS: WmsKpiDef = { key: 'skus', label: 'SKUs', dataKey: 'skus', icon: Package, tone: 'text-blue-700 bg-blue-50 border-blue-200', route: '/wms/master-data' };
const KPI_INBOUND: WmsKpiDef = { key: 'inbound', label: 'Inbound Orders', dataKey: 'inboundOrders', icon: ArrowDownLeft, tone: 'text-orange-700 bg-orange-50 border-orange-200', route: '/wms/inbound' };
const KPI_OUTBOUND: WmsKpiDef = { key: 'outbound', label: 'Outbound Orders', dataKey: 'outboundOrders', icon: TruckIcon, tone: 'text-emerald-700 bg-emerald-50 border-emerald-200', route: '/wms/outbound' };
const KPI_CYCLE_COUNT: WmsKpiDef = { key: 'cycleCount', label: 'Stock Take', dataKey: 'cycleCounts', icon: ClipboardList, tone: 'text-violet-700 bg-violet-50 border-violet-200', route: '/wms/stock-take' };
const KPI_INVENTORY: WmsKpiDef = { key: 'inventory', label: 'Inventory Records', dataKey: 'inventoryRecords', icon: Layers, tone: 'text-cyan-700 bg-cyan-50 border-cyan-200', route: '/wms/inventory' };
const KPI_PICK_LISTS: WmsKpiDef = { key: 'pickLists', label: 'Pick Lists', dataKey: 'pickLists', icon: ClipboardCheck, tone: 'text-amber-700 bg-amber-50 border-amber-200', route: '/wms/work-orders' };

// ═══════════════════════════════════════════════════
//  PROFILE PRESETS
// ═══════════════════════════════════════════════════

const CFS_PROFILE: WmsProfile = {
  key: 'cfs',
  label: 'CFS Warehouse',
  description: 'Container freight station with lot tracking, docking, stuffing, and dwell control',
  theme: {
    heroGradient: 'from-indigo-950 via-indigo-900 to-sky-800',
    heroIcon: Container,
    heroLabel: 'CFS Control Tower',
    heroSubtitle: 'Arrival Notice → Gate / Dock → Unstuff → Sort by HBL / Lot → Store / Release.',
  },
  kpis: [
    KPI_INBOUND,
    KPI_OUTBOUND,
    KPI_INVENTORY,
    KPI_PICK_LISTS,
    KPI_CYCLE_COUNT,
    KPI_SKUS,
  ],
  flowSteps: [
    { key: 'arrival-notice', phase: 'Source', title: 'Arrival Notice', desc: 'Capture container, HBL, and ETA before dock planning.', route: '/wms/documents', icon: FileUp },
    { key: 'gate-dock', phase: 'Gate', title: 'Gate / Dock', desc: 'Plan dock slot and register truck or container arrival.', route: '/wms/arrivals', icon: CalendarDays },
    { key: 'unstuff', phase: 'Inbound', title: 'Unstuff', desc: 'Unload container cargo into inbound holding or sort zone.', route: '/wms/inbound', icon: ArrowDownLeft },
    { key: 'sort-lot', phase: 'Sort', title: 'Sort by HBL / Lot', desc: 'Group cargo by owner, HBL, lot, or bonded handling requirement.', route: '/wms/work-orders', icon: ListOrdered },
    { key: 'hold-release', phase: 'Control', title: 'Hold / Release', desc: 'Apply inspection, customs, or free-time release control.', route: '/wms/inspection', icon: ShieldCheck },
    { key: 'stuff-release', phase: 'Outbound', title: 'Stuff / Release', desc: 'Stuff export cargo or release import cargo to outbound handoff.', route: '/wms/outbound', icon: Truck },
  ],
  visibleNavIds: [
    'wms-dashboard', 'wms-tasks', 'wms-inbound', 'wms-inspection',
    'wms-inventory', 'wms-outbound', 'wms-work-orders', 'wms-arrivals',
    'wms-schedule', 'wms-stock-ledger', 'wms-master-data', 'wms-documents',
    'wms-3pl', 'wms-supervisor', 'wms-boss', 'wms-cs',
  ],
  quickLinks: [
    { label: 'Arrival Dock', desc: 'Manage inbound dock windows and gate queue', route: '/wms/arrivals', icon: CalendarDays },
    { label: 'Inbound Board', desc: 'Track unstuffing, receiving, and exceptions', route: '/wms/inbound', icon: ArrowDownLeft },
    { label: 'Schedule Board', desc: 'Coordinate dock, free-time, and release timing', route: '/wms/schedule', icon: Timer },
    { label: 'Shared Documents', desc: 'Arrival notice, HBL, release, and supporting files', route: '/wms/documents', icon: FileSpreadsheet },
  ],
};

const ECOMMERCE_PROFILE: WmsProfile = {
  key: 'ecommerce',
  label: 'Ecommerce Warehouse',
  description: 'High-volume pick-pack-ship with returns handling',
  theme: {
    heroGradient: 'from-violet-950 via-violet-900 to-purple-800',
    heroIcon: Rocket,
    heroLabel: 'Ecommerce Fulfillment Center',
    heroSubtitle: 'Order → Pick → Pack → Ship → Returns — optimised for speed and accuracy.',
  },
  kpis: [
    KPI_OUTBOUND,
    KPI_PICK_LISTS,
    { key: 'orders', label: 'Pending Orders', dataKey: 'outboundOrders', icon: ShoppingCart, tone: 'text-rose-700 bg-rose-50 border-rose-200', route: '/wms/outbound' },
    KPI_INBOUND,
    KPI_INVENTORY,
    KPI_SKUS,
  ],
  flowSteps: [
    { key: 'import-orders', phase: 'Source', title: 'Import Orders', desc: 'Receive sales orders from marketplace or API.', route: '/wms/documents', icon: FileUp },
    { key: 'wave-plan', phase: 'Plan', title: 'Wave Plan', desc: 'Group orders into pick waves by zone or carrier.', route: '/wms/work-orders', icon: ListOrdered },
    { key: 'pick', phase: 'Execute', title: 'Pick', desc: 'Pick SKUs from storage bins using scan or cart.', route: '/wms/tasks', icon: ClipboardCheck },
    { key: 'pack', phase: 'Execute', title: 'Pack', desc: 'Pack items, weigh, and label cartons.', route: '/wms/outbound', icon: PackageCheck },
    { key: 'ship', phase: 'Dispatch', title: 'Ship', desc: 'Hand off to carrier, generate tracking.', route: '/wms/outbound', icon: Truck },
    { key: 'returns', phase: 'Reverse', title: 'Returns', desc: 'Receive returns, QC inspect, and restock.', route: '/wms/inbound', icon: Undo2 },
  ],
  visibleNavIds: [
    'wms-dashboard', 'wms-tasks', 'wms-inbound', 'wms-inventory',
    'wms-outbound', 'wms-work-orders', 'wms-documents',
    'wms-schedule', 'wms-stock-ledger', 'wms-master-data',
    'wms-supervisor', 'wms-boss', 'wms-cs',
  ],
  quickLinks: [
    { label: 'Pick Waves', desc: 'Create & manage pick waves', route: '/wms/work-orders', icon: ListOrdered },
    { label: 'Ship Queue', desc: 'Orders ready to dispatch', route: '/wms/outbound', icon: Truck },
    { label: 'Returns', desc: 'Process customer returns', route: '/wms/inbound', icon: Undo2 },
    { label: 'Tasks', desc: 'Active floor tasks', route: '/wms/tasks', icon: Activity },
  ],
};

const GENERAL_PROFILE: WmsProfile = {
  key: 'general',
  label: 'General Warehouse',
  description: 'PO-based receiving, putaway, storage, and dispatch',
  theme: {
    heroGradient: 'from-slate-950 via-slate-900 to-slate-800',
    heroIcon: Warehouse,
    heroLabel: 'Warehouse Control Tower',
    heroSubtitle: 'PO → Receive → Putaway → Inventory → Dispatch — full inbound-to-outbound visibility.',
  },
  kpis: [KPI_SKUS, KPI_INBOUND, KPI_OUTBOUND, KPI_CYCLE_COUNT, KPI_INVENTORY, KPI_PICK_LISTS],
  flowSteps: [
    { key: 'upload', phase: 'Source', title: 'Upload File', desc: 'Upload vendor files or customer documents before import.', route: '/wms/documents', icon: FileUp },
    { key: 'import-po', phase: 'Prepare', title: 'Import PO', desc: 'Create purchase orders from uploaded files.', route: '/wms/purchase-orders?openImport=1&importType=purchase-orders', icon: FileSpreadsheet },
    { key: 'review-po', phase: 'Prepare', title: 'Review PO', desc: 'Check imported PO headers, lines, and supplier details.', route: '/wms/purchase-orders', icon: SearchCheck },
    { key: 'receive', phase: 'Inbound', title: 'Receive', desc: 'Start PO receiving workbench and create SRF.', route: '/wms/inbound/new?mode=PO', icon: ArrowDownLeft },
    { key: 'inbound', phase: 'Inbound', title: 'Inbound Board', desc: 'Track SRF, receipt progress, and receiving exceptions.', route: '/wms/inbound', icon: Warehouse },
    { key: 'inspection', phase: 'Quality', title: 'Inspection', desc: 'Run QC checks, review photos, close quality tasks.', route: '/wms/inspection', icon: ShieldCheck },
    { key: 'putaway', phase: 'Execution', title: 'Task Queue', desc: 'Move stock into storage bins via putaway tasks.', route: '/wms/tasks', icon: ClipboardCheck },
    { key: 'inventory', phase: 'Stock', title: 'Inventory', desc: 'Verify stock positions and bin balances.', route: '/wms/inventory', icon: Boxes },
    { key: 'cycle-count', phase: 'Control', title: 'Stock Take', desc: 'Plan counts, generate lines, and review variances.', route: '/wms/stock-take', icon: ClipboardList },
  ],
  // general shows almost everything
  visibleNavIds: undefined,
  quickLinks: [
    { label: 'Documents', desc: 'Upload files and open PO import', route: '/wms/documents', icon: FileUp },
    { label: 'PO', desc: 'Review purchase orders and import history', route: '/wms/purchase-orders', icon: FileSpreadsheet },
    { label: 'Schedule Board', desc: 'Plan dock slots, tasks, and daily warehouse flow', route: '/wms/schedule', icon: CalendarDays },
    { label: 'Tasks', desc: 'Putaway, pick, release tasks', route: '/wms/tasks', icon: Activity },
  ],
};

const MANUFACTURING_PROFILE: WmsProfile = {
  key: 'manufacturing',
  label: 'Manufacturing Stores',
  description: 'BOM-driven material requests, issue, return, and production tracking',
  theme: {
    heroGradient: 'from-amber-950 via-amber-900 to-orange-800',
    heroIcon: Cog,
    heroLabel: 'Manufacturing Stores',
    heroSubtitle: 'BOM → Material Request → Issue → Production → Return → Inspect.',
  },
  kpis: [
    KPI_SKUS,
    { key: 'materialReqs', label: 'Material Requests', dataKey: 'outboundOrders', icon: ClipboardList, tone: 'text-amber-700 bg-amber-50 border-amber-200', route: '/wms/material-requests' },
    KPI_INBOUND,
    KPI_INVENTORY,
    KPI_CYCLE_COUNT,
    { key: 'issueOrders', label: 'Issue Orders', dataKey: 'pickLists', icon: ClipboardCheck, tone: 'text-rose-700 bg-rose-50 border-rose-200', route: '/wms/work-orders' },
  ],
  flowSteps: [
    { key: 'bom', phase: 'Plan', title: 'BOM / Recipe', desc: 'Define bill of materials or production recipe.', route: '/wms/master-data', icon: FileSpreadsheet },
    { key: 'material-req', phase: 'Request', title: 'Material Request', desc: 'Create material request against production order.', route: '/wms/material-requests', icon: ClipboardList },
    { key: 'issue', phase: 'Execute', title: 'Issue Material', desc: 'Issue raw materials to production floor.', route: '/wms/work-orders', icon: ClipboardCheck },
    { key: 'production', phase: 'Production', title: 'Production', desc: 'Track WIP and production completion.', route: '/wms/tasks', icon: Cog },
    { key: 'return', phase: 'Reverse', title: 'Return Surplus', desc: 'Return unused materials to store.', route: '/wms/inbound', icon: Undo2 },
    { key: 'qc', phase: 'Quality', title: 'QC Inspect', desc: 'Inspect finished goods before shelving.', route: '/wms/inspection', icon: ShieldCheck },
    { key: 'fg-putaway', phase: 'Stock', title: 'FG Putaway', desc: 'Put finished goods into storage.', route: '/wms/tasks', icon: Boxes },
  ],
  visibleNavIds: [
    'wms-dashboard', 'wms-tasks', 'wms-inbound', 'wms-inspection',
    'wms-inventory', 'wms-work-orders', 'wms-material-requests',
    'wms-schedule', 'wms-stock-ledger', 'wms-master-data',
    'wms-supervisor', 'wms-boss', 'wms-cs',
  ],
  quickLinks: [
    { label: 'Material Requests', desc: 'Create & track material requests', route: '/wms/material-requests', icon: ClipboardList },
    { label: 'Issue Orders', desc: 'Issue materials to production', route: '/wms/work-orders', icon: ClipboardCheck },
    { label: 'Inventory', desc: 'Check raw material & FG stock', route: '/wms/inventory', icon: Boxes },
    { label: 'Tasks', desc: 'Floor tasks for production store', route: '/wms/tasks', icon: Activity },
  ],
};

const DISTRIBUTION_PROFILE: WmsProfile = {
  key: 'distribution',
  label: 'Distribution Center',
  description: 'High-throughput cross-docking, sorting, and dispatch',
  theme: {
    heroGradient: 'from-emerald-950 via-emerald-900 to-teal-800',
    heroIcon: Forklift,
    heroLabel: 'Distribution Hub',
    heroSubtitle: 'Receive → Cross-Dock → Sort → Stage → Dispatch — speed is everything.',
  },
  kpis: [
    KPI_INBOUND,
    KPI_OUTBOUND,
    { key: 'throughput', label: 'Throughput', dataKey: 'inventoryRecords', icon: BarChart3, tone: 'text-teal-700 bg-teal-50 border-teal-200', route: '/wms/inventory' },
    KPI_PICK_LISTS,
    KPI_SKUS,
    KPI_CYCLE_COUNT,
  ],
  flowSteps: [
    { key: 'receive', phase: 'Inbound', title: 'Receive', desc: 'Unload trucks and scan in cargo.', route: '/wms/inbound', icon: ArrowDownLeft },
    { key: 'cross-dock', phase: 'Route', title: 'Cross-Dock', desc: 'Route cargo directly to outbound dock.', route: '/wms/tasks', icon: Repeat },
    { key: 'sort', phase: 'Route', title: 'Sort', desc: 'Sort goods by destination or carrier.', route: '/wms/work-orders', icon: ListOrdered },
    { key: 'stage', phase: 'Staging', title: 'Stage', desc: 'Stage pallets at dispatch doors.', route: '/wms/tasks', icon: Boxes },
    { key: 'dispatch', phase: 'Dispatch', title: 'Dispatch', desc: 'Load trucks and mark shipment.', route: '/wms/outbound', icon: Truck },
    { key: 'track', phase: 'Track', title: 'Track', desc: 'Monitor delivery and exceptions.', route: '/wms/outbound', icon: Timer },
  ],
  visibleNavIds: [
    'wms-dashboard', 'wms-tasks', 'wms-inbound', 'wms-inventory',
    'wms-outbound', 'wms-work-orders', 'wms-arrivals',
    'wms-schedule', 'wms-stock-ledger', 'wms-master-data', 'wms-3pl',
    'wms-supervisor', 'wms-boss', 'wms-cs',
  ],
  quickLinks: [
    { label: 'Arrival Dock', desc: 'Trucks waiting to unload', route: '/wms/arrivals', icon: ArrowDownLeft },
    { label: 'Dispatch Queue', desc: 'Orders staged for dispatch', route: '/wms/outbound', icon: Truck },
    { label: 'Schedule Board', desc: 'Coordinate dock timing and outbound waves', route: '/wms/schedule', icon: CalendarDays },
    { label: 'Tasks', desc: 'Cross-dock & sort tasks', route: '/wms/tasks', icon: Activity },
    { label: 'Throughput', desc: 'Today\'s throughput stats', route: '/wms/inventory', icon: BarChart3 },
  ],
};

const YARD_PROFILE: WmsProfile = {
  key: 'yard',
  label: 'Yard Management',
  description: 'Gate control, parking, container stuffing/unstuffing',
  theme: {
    heroGradient: 'from-sky-950 via-sky-900 to-blue-800',
    heroIcon: Container,
    heroLabel: 'Yard Control',
    heroSubtitle: 'Gate In → Park → Stuff / Unstuff → Gate Out — container yard operations.',
  },
  kpis: [
    KPI_INBOUND,
    KPI_OUTBOUND,
    KPI_INVENTORY,
    KPI_SKUS,
    KPI_CYCLE_COUNT,
    KPI_PICK_LISTS,
  ],
  flowSteps: [
    { key: 'gate-in', phase: 'Gate', title: 'Gate In', desc: 'Register truck arrival and container ID.', route: '/wms/arrivals', icon: ArrowDownLeft },
    { key: 'parking', phase: 'Yard', title: 'Park', desc: 'Assign parking slot for container.', route: '/wms/tasks', icon: Boxes },
    { key: 'unstuffing', phase: 'Operations', title: 'Unstuff', desc: 'Unload container and receive cargo.', route: '/wms/inbound', icon: Package },
    { key: 'stuffing', phase: 'Operations', title: 'Stuff', desc: 'Load cargo into container for export.', route: '/wms/outbound', icon: Container },
    { key: 'inspection', phase: 'Quality', title: 'Inspect', desc: 'Check cargo condition and seal.', route: '/wms/inspection', icon: Shield },
    { key: 'gate-out', phase: 'Gate', title: 'Gate Out', desc: 'Release truck with completed container.', route: '/wms/outbound', icon: Truck },
  ],
  visibleNavIds: [
    'wms-dashboard', 'wms-tasks', 'wms-inbound', 'wms-inspection',
    'wms-inventory', 'wms-outbound', 'wms-arrivals',
    'wms-schedule', 'wms-stock-ledger', 'wms-master-data',
    'wms-supervisor', 'wms-boss', 'wms-cs',
  ],
  quickLinks: [
    { label: 'Arrival Dock', desc: 'Trucks waiting at gate', route: '/wms/arrivals', icon: ArrowDownLeft },
    { label: 'Schedule Board', desc: 'Coordinate gate windows and yard moves', route: '/wms/schedule', icon: CalendarDays },
    { label: 'Yard Tasks', desc: 'Stuff/unstuff/parking tasks', route: '/wms/tasks', icon: Activity },
    { label: 'Inspection', desc: 'Cargo QC and seal checks', route: '/wms/inspection', icon: ShieldCheck },
    { label: 'Gate Out', desc: 'Dispatch & release queue', route: '/wms/outbound', icon: Truck },
  ],
};

// ─── Hybrid = everything visible ───────────────────

const HYBRID_PROFILE: WmsProfile = {
  ...GENERAL_PROFILE,
  key: 'hybrid',
  label: 'Hybrid Warehouse',
  description: 'Full-featured — shows all KPIs, flows, and nav items',
  visibleNavIds: undefined,
};

// ═══════════════════════════════════════════════════
//  REGISTRY
// ═══════════════════════════════════════════════════

export const WMS_PROFILES: Record<WmsProfileKey, WmsProfile> = {
  cfs: CFS_PROFILE,
  ecommerce: ECOMMERCE_PROFILE,
  general: GENERAL_PROFILE,
  manufacturing: MANUFACTURING_PROFILE,
  distribution: DISTRIBUTION_PROFILE,
  yard: YARD_PROFILE,
  hybrid: HYBRID_PROFILE,
};

/**
 * Map legacy / human-readable mode strings to our profile key.
 * E.g. '3pl' → 'general', 'metals' → 'manufacturing', 'ecommerce warehouse' → 'ecommerce'.
 */
const ALIAS_MAP: Record<string, WmsProfileKey> = {
  '3pl': 'general',
  metals: 'manufacturing',
  'ecommerce warehouse': 'ecommerce',
  'e-commerce warehouse': 'ecommerce',
  'ecommerce-warehouse': 'ecommerce',
};

export function resolveProfileKey(raw?: string | null): WmsProfileKey {
  if (!raw) return 'hybrid';
  const lower = raw.toLowerCase().trim();
  if (lower in WMS_PROFILES) return lower as WmsProfileKey;
  return ALIAS_MAP[lower] ?? 'hybrid';
}

export function getWmsProfile(raw?: string | null): WmsProfile {
  return WMS_PROFILES[resolveProfileKey(raw)];
}
